import { z } from 'zod';
import { ACCESS_AREAS, CLINIC_SECTIONS, LASER_STAFF_ROLES, ORG_TYPES, PERMISSIONS, ROLES } from './access.js';
import { AuthUserSchema } from './auth.js';
import { DisplayNameSchema, IsoDateTimeSchema, PhoneInputSchema, UuidSchema, pageSchema } from './common.js';

export const OrgTypeSchema = z.enum(ORG_TYPES);
export const RoleSchema = z.enum(ROLES);
export const PermissionSchema = z.enum(PERMISSIONS);

export const RolesSchema = z
  .array(RoleSchema)
  .min(1, { message: 'دست‌کم یک نقش انتخاب کنید.' })
  .max(ROLES.length)
  .refine((roles) => new Set(roles).size === roles.length, { message: 'نقش تکراری است.' });

export const OrganizationSchema = z.object({
  id: UuidSchema,
  type: OrgTypeSchema,
  name: z.string(),
  slug: z.string(),
  status: z.enum(['active', 'suspended']),
  createdAt: IsoDateTimeSchema,
});
export type Organization = z.infer<typeof OrganizationSchema>;

export const ClinicProfileSchema = z.object({
  phone: z.string().nullable(),
  address: z.string().nullable(),
});

export const CurrentOrgResponseSchema = z.object({
  organization: OrganizationSchema,
  clinicProfile: ClinicProfileSchema.nullable(),
});
export type CurrentOrgResponse = z.infer<typeof CurrentOrgResponseSchema>;

export const MyMembershipSchema = z.object({
  membershipId: UuidSchema,
  roles: z.array(RoleSchema),
  permissions: z.array(PermissionSchema),
  /** When this member finished the welcome tour; null shows it at the next login. */
  onboardedAt: IsoDateTimeSchema.nullable(),
  organization: OrganizationSchema.pick({ id: true, type: true, name: true, slug: true }),
});
export type MyMembership = z.infer<typeof MyMembershipSchema>;

export const MeResponseSchema = z.object({
  user: AuthUserSchema,
  memberships: z.array(MyMembershipSchema),
  /** Without SMS codes only an account the platform vouched for may open a clinic (ADR-033). */
  canCreateClinic: z.boolean(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const UpdateProfileBodySchema = z.object({
  displayName: DisplayNameSchema,
});
export type UpdateProfileBody = z.input<typeof UpdateProfileBodySchema>;

/** Only the name is accepted. Type is always `clinic`; id and slug are server-generated. */
export const CreateClinicBodySchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: 'نام کلینیک دست‌کم ۲ نویسه است.' })
    .max(200, { message: 'نام کلینیک حداکثر ۲۰۰ نویسه است.' }),
});
export type CreateClinicBody = z.input<typeof CreateClinicBodySchema>;

export const MemberSchema = z.object({
  id: UuidSchema,
  user: AuthUserSchema,
  roles: z.array(RoleSchema),
  /** null = the roles' defaults; otherwise exactly the areas the admin ticked (ADR-042). */
  accessAreas: z.array(z.enum(ACCESS_AREAS)).nullable(),
  status: z.enum(['active', 'removed']),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Member = z.infer<typeof MemberSchema>;

/** The admin's ticks for a member (ADR-042); null puts the member back on the roles' defaults. */
export const MemberAccessInputSchema = z.object({
  areas: z
    .array(z.enum(ACCESS_AREAS))
    .max(ACCESS_AREAS.length)
    .refine((v) => new Set(v).size === v.length, { message: 'مورد تکراری است.' })
    .nullable(),
});
export type MemberAccessInput = z.input<typeof MemberAccessInputSchema>;

export const CreateClinicResponseSchema = z.object({
  organization: OrganizationSchema,
  membership: MemberSchema,
});
export type CreateClinicResponse = z.infer<typeof CreateClinicResponseSchema>;

/** The name is required (pilot feedback #2); it is applied only to an account without one. */
export const AddMemberBodySchema = z.object({
  phone: PhoneInputSchema,
  displayName: z.preprocess((v) => v ?? '', DisplayNameSchema),
  roles: RolesSchema,
});
export type AddMemberBody = z.input<typeof AddMemberBodySchema>;

/** A name too, for a member who has none yet (pilot feedback #2; same rule as when adding). */
export const SetMemberRolesBodySchema = z.object({
  roles: RolesSchema,
  displayName: DisplayNameSchema.optional(),
});
export type SetMemberRolesBody = z.input<typeof SetMemberRolesBodySchema>;

export const MemberListResponseSchema = pageSchema(MemberSchema);
export type MemberListResponse = z.infer<typeof MemberListResponseSchema>;

export const ClinicSectionSchema = z.enum(CLINIC_SECTIONS);

/** A non-empty set of the receptionist roles that may handle a part of the laser section (ADR-036). */
const LaserStaffRolesSchema = z
  .array(z.enum(LASER_STAFF_ROLES))
  .min(1, { message: 'دست‌کم یکی را انتخاب کنید.' })
  .max(LASER_STAFF_ROLES.length)
  .refine((roles) => new Set(roles).size === roles.length, { message: 'نقش تکراری است.' });

/** Who handles the laser desk (appointments, check-in) and the laser till (balances, payments). */
export const SectionSettingsInputSchema = z.object({
  /** Whether the clinic has a laser section (ADR-043); left out = unchanged. */
  laserEnabled: z.boolean().optional(),
  laserDeskRoles: LaserStaffRolesSchema,
  laserCashRoles: LaserStaffRolesSchema,
});
export type SectionSettingsInput = z.input<typeof SectionSettingsInputSchema>;

export const MySectionsSchema = z.object({
  desk: z.array(ClinicSectionSchema),
  cash: z.array(ClinicSectionSchema),
  clinical: z.array(ClinicSectionSchema),
});
export type MySections = z.infer<typeof MySectionsSchema>;

export const SectionSettingsSchema = z.object({
  /** No laser: nothing of the laser section is shown or written, for anybody (ADR-043). */
  laserEnabled: z.boolean(),
  laserDeskRoles: z.array(z.enum(LASER_STAFF_ROLES)),
  laserCashRoles: z.array(z.enum(LASER_STAFF_ROLES)),
  updatedAt: IsoDateTimeSchema.nullable(),
  /** The sections the caller reaches, from the same SQL function RLS uses. */
  mine: MySectionsSchema,
});
export type SectionSettings = z.infer<typeof SectionSettingsSchema>;

/** What turning laser off hides (ADR-043): shown before the admin confirms. */
export const LaserUsageSchema = z.object({
  /** Booked, confirmed, checked in or in progress. */
  openAppointments: z.number().int(),
  activePackages: z.number().int(),
  patientsWithBalance: z.number().int(),
  services: z.number().int(),
  members: z.number().int(),
  openProviderReceivables: z.number().int().default(0),
});
export type LaserUsage = z.infer<typeof LaserUsageSchema>;
